/* globals lg, Shopify, theme */

// #region Imports
import { containsAllClasses, getQueryStringParameters, isHomepage, isPage } from '@js/helpers';

import GatoSec from '@js/components/GatoSec/GatoSec';
// import { getSprCallbacks } from '@js/components/spr';
import { fbChat } from '@js/components/fb-chat';
import { fbPixel } from '@js/components/fb-pixel';
import { onGuestBookAddReviewClick } from '@js/components/guestbook';
import { initPhotoswipe } from '@js/components/lg-photoswipe';
import { onQuickAddClick } from '@js/components/product-grid';
import { initColorSelector } from '@js/components/lg-color-selector';
import { initProductTestimonials } from '@js/components/product-testimonials';
import { i18nLoad } from './i18n';
import {
  IMPULSE_LOADED_EVENT,
  NAVBAR_MOBILE_MAX_BREAKPOINT,
  SAVE_WISHLIST_URL,
  STICKY_HEADER_HEIGHT,
} from './constants';

// #endregion

let IMPULSE_LOADED = false;

const smoothScrollSupported = 'scrollBehavior' in document.documentElement.style;
const pageContainer = document.querySelector('#PageContainer');
const headerWrapper = document.querySelector('#HeaderWrapper');
const isStickyHeader = headerWrapper ? headerWrapper.classList.contains('header-wrapper--sticky') : false;

// TODO: remove if unnecessary
// TEST : Force bound newsletter forms to hcaptcha
/*
const newsletterForms = document.querySelectorAll("form[id^='newsletter-']");
newsletterForms.forEach((form) => {
  window.Shopify.captcha.protect(form, () => {});
});
*/

// #region Fetch Interceptor
/** Contient des fonctions callables à l'interception des requêtes */
const fetchHooks = [];

const { fetch: origFetch } = window;
window.fetch = async (...args) => {
  console.log('fetch called with args:', args);
  const response = await origFetch(...args);
  console.log('response', response);

  fetchHooks.forEach((hook) => hook(args, response));

  return response;
};
// #endregion

// #region Helpers
/**
 * Change le style du menu sur la home selon que le slide courant est clair ou sombre
 * @param {number} index l'index de la slide active
 */
function syncHeaderLightnessWithActiveSlide() {
  const flickity = this;
  const selectedIndex = flickity.selectedIndex;
  const isCollapsedMenu = window.innerWidth <= NAVBAR_MOBILE_MAX_BREAKPOINT;

  const isDark =
    (isCollapsedMenu
      ? flickity.cells[selectedIndex]?.element?.dataset?.lightMobile
      : flickity.cells[selectedIndex]?.element?.dataset?.light) === 'dark';

  if (isDark) {
    headerWrapper.classList.add('is-light');
  } else {
    headerWrapper.classList.remove('is-light');
  }
}
// #endregion

// #region Init functions
/**
 * Init Cookie consent popup
 */
function initGatosec() {
  const gatoSecSettings = lg.i18n.gatosec;
  gatoSecSettings.moreInfoLink = Shopify.routes.root + 'policies/privacy-policy';
  gatoSecSettings.delay = 300;
  gatoSecSettings.onAccept = function () {
    if (this.getPreference('social')) {
      fbChat.load(lg.chat.lazy);
    } else {
      fbChat.deleteCookies();
    }

    if (this.getPreference('analytics')) {
      // if (lg.analytics.tagManagerId) {
      // loadGoogleAnalytics(lg.analytics.tagManagerId);
      // }
      if (lg.analytics.fbPixelId) {
        fbPixel.load(lg.analytics.fbPixelId);
      }
    } else {
      fbPixel.deleteCookies();
    }
  };
  lg.cookies = new GatoSec(gatoSecSettings);
  lg.cookies.init();
}

/**
 * Init Producer Maps
 */
function initProducerMaps() {
  if (lg.maps.length) {
    import(
      /* webpackChunkName: "TeaWorldMap" */
      '@js/TeaWorldMap/TeaWorldMap'
    ).then(({ default: TeaWorldMap }) => {
      lg.maps = lg.maps.map((values) => {
        const worldMap = new TeaWorldMap(
          values.selector,
          values.countryCode,
          values.provinceCode,
          values.customProvinceName
        );
        worldMap.init();
        return worldMap;
      });
    });
  }
}

/**
 * Init Store Locator
 */
function initStoreLocator() {
  if (lg.storeLocator !== undefined) {
    import(
      /* webpackChunkName: "StoreLocator" */
      '@js/StoreLocator'
    ).then(({ default: StoreLocator }) => {
      const storeLocator = new StoreLocator(lg.storeLocator);
      storeLocator.init();
    });
  }
}

/** Custom function to init Judge.me widget with less page jank */
function initJudgeMe() {
  // debugger;
  const jdgmProductReviews = document.querySelector('#judgeme_product_reviews');
  if (jdgmProductReviews && window.jdgmSettings) {
    try {
      const widgetTitle = jdgmProductReviews.querySelector('.jdgm-rev-widg__title');
      const widgetSummaryText = jdgmProductReviews.querySelector('.jdgm-rev-widg__summary-text');
      const widgetWriteReviewLink = document.querySelector('.jdgm-write-rev-link');

      const widget = jdgmProductReviews.querySelector('.jdgm-rev-widg');
      // console.log('widget', widget);
      // if (widget) {
      const numReviews = widget.getAttribute('data-number-of-reviews');
      widgetSummaryText.innerHTML = window.jdgmSettings.widget_summary_text.replace(
        '{{ number_of_reviews }}',
        numReviews
      );
      // }

      widgetTitle.innerHTML = window.jdgmSettings.widget_title;
      widgetWriteReviewLink.innerHTML = window.jdgmSettings.widget_open_form_text;

      // add our custom class
      widgetWriteReviewLink.classList.add('btn');
      // debugger;

      window.requestAnimationFrame(() => {
        const jdgmProductReviewsHeight = jdgmProductReviews.offsetHeight;
        // force set the height
        jdgmProductReviews.style.height = jdgmProductReviewsHeight + 'px';
        jdgmProductReviews.classList.add('header-ready');
      });
    } catch (error) {
      jdgmProductReviews.classList.add('header-ready');
    }
  }
}

/** Init our custom wishlist button based on Wishlist Engine */
function initWishlist() {
  const singleCustomWishlistButtons = document.querySelector('.wishlist-engine.wishlist-engine--lg');
  if (singleCustomWishlistButtons) {
    singleCustomWishlistButtons.addEventListener('click', () => {
      singleCustomWishlistButtons.classList.add('wishlist-engine-button-sending');
    });

    fetchHooks.push((args, response) => {
      if (args[0] === SAVE_WISHLIST_URL) {
        if (singleCustomWishlistButtons) {
          singleCustomWishlistButtons.classList.remove('wishlist-engine-button-sending');
        }
      }
    });
  }
}

/**
 * Main theme init
 */
async function initFirst() {
  console.warn('======== initFirst');

  // TODO: should not be blocking
  lg.i18n = await i18nLoad();

  initGatosec();
  initProducerMaps();
  initStoreLocator();
  initJudgeMe();
  initWishlist();
  initColorSelector();
}
// #endregion

// #region Event handlers
function handleOnDomLoadedHashAnchor() {
  const hash = window.location.hash.split('?')[0];

  if (smoothScrollSupported && hash.indexOf('#') === 0) {
    const element = document.querySelector(hash);
    window.setTimeout(() => {
      if (element) {
        // event.preventDefault();
        const yOffset = -STICKY_HEADER_HEIGHT - 0; //sticky header height - offset
        const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({
          top: y,
          // behavior: 'smooth'
        });
      }
    }, 10);
  }
}

function handleQueryStringParameters(params) {
  // detect customer_posted
  if (params.customer_posted === 'true') {
    document.body.classList.add('customer-posted');
    const popup = document.getElementById('NewsletterPopup-newsletter-popup');
    if (popup) {
      // display result immediately
      popup.setAttribute('data-delay-seconds', 0);
    }
  }
}

/**
 * Intercept clicks on sliders to change default behavior
 * Click on slide will pause and go to next slide (except clicking on link)
 *
 * @param {Event} event Original event object
 * @param {Event | Touch} pointer The event object that has .pageX and .pageY
 * @param {Element} cellElement If a cell was clicked, the element
 * @param {Integer} cellIndex If a cell was clicked, the cell’s zero-based number
 */
function handleSlideshowClick(event, pointer, cellElement, cellIndex) {
  // Do nothing if clicking inside button link
  if (event.target.parentNode.className === 'hero__link') return;

  event.preventDefault();
  event.stopPropagation();

  const flickity = this;
  flickity.pausePlayer();
  flickity.select(flickity.selectedIndex + 1);
}

function handleAnchorClick(event) {
  const href = event.target.getAttribute('href');

  // Handle smooth scrolling to target anchor
  if (smoothScrollSupported && href.indexOf('#') === 0) {
    const element = document.querySelector(href);
    if (element) {
      event.preventDefault();
      const yOffset = -STICKY_HEADER_HEIGHT - 20; //sticky header height - offset
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }
}

function handlePageContainerClick(event) {
  if (IMPULSE_LOADED && event.target.classList.contains('lg-quick-add')) {
    onQuickAddClick(event);
  } else if (isPage('guestbook') && containsAllClasses(event.target, ['btn', 'toggle__label'])) {
    onGuestBookAddReviewClick(event);
  } else if (event.target.nodeName === 'A') {
    handleAnchorClick(event);
  }
}
// #endregion

// #region Events

/**
 * Trigger on DOM loaded
 */
window.addEventListener('DOMContentLoaded', function () {
  console.warn('======== initOnDomLoaded');
  handleQueryStringParameters(getQueryStringParameters());

  // TODO: improve this
  handleOnDomLoadedHashAnchor();
});

/**
 * Trigger on Impulse theme loaded
 * Functions inside need to wait for Impulse theme to be fully loaded
 */
document.addEventListener(IMPULSE_LOADED_EVENT, function () {
  console.warn('======== initOnImpulseLoaded');
  if (isHomepage()) {
    const mainSlideshowInstance = theme.sections.instances.find(
      (instance) => instance.type === 'slideshow-section' && instance.initialIndex === 0
    );

    if (mainSlideshowInstance) {
      const flickity = window.Flickity.data(mainSlideshowInstance.slideshow);
      flickity.on('staticClick', handleSlideshowClick.bind(flickity));

      if (isStickyHeader) {
        flickity.on('change', syncHeaderLightnessWithActiveSlide);
        syncHeaderLightnessWithActiveSlide.bind(flickity)();
      }
    }

    initProductTestimonials();
  }

  // Media grid
  initPhotoswipe();

  IMPULSE_LOADED = true;
});

/**
 * Trigger on JudgeMe loaded
 */
document.addEventListener('jdgm.finishLoadingCore', () => {
  // initJudgeMe();

  console.warn('JUDGEME: finishLoadingCore');

  /**
   // HACK: Hijack judge.me scrollTo function to add some offset
   */
  const original = window.jdgm.scrollTo;
  window.jdgm.scrollTo = (t, n) => {
    console.log('n', n);
    console.log('t', t);

    const siteHeader = document.querySelector('#SiteHeader');
    const height = siteHeader.offsetHeight;
    console.log('height', height);

    let nUpdated = 73;
    if (window.innerWidth > NAVBAR_MOBILE_MAX_BREAKPOINT) {
      nUpdated = STICKY_HEADER_HEIGHT;
    }

    return original(t, nUpdated);
  };

  const jdgmProductReviews = document.querySelector('#judgeme_product_reviews');
  setTimeout(() => {
    jdgmProductReviews.style.height = '';
    jdgmProductReviews.classList.add('reviews-ready');
  }, 100);
});

/** Triggered when wishlist 'page' popup has finished loading */
document.addEventListener('wishlist:page:loaded', (event) => {
  console.warn('———————————— WISHLIST PAGE LOADED ————————');
  console.log('event', event);
});

pageContainer.addEventListener('click', (event) => handlePageContainerClick(event));
// #endregion

// #region Shopify Editor
if (Shopify.designMode) {
  document.addEventListener('shopify:block:select', function (evt) {
    const instance = theme.sections.instances.find((instance) => instance.id === evt.detail.sectionId);
    if (instance.type === 'slideshow-section') {
      // If section reloads, slideshow might not have been setup yet, wait a bit before trying
      setTimeout(() => {
        syncHeaderLightnessWithActiveSlide.bind(window.Flickity.data(instance.slideshow))();
      }, 300);
    }
  });
}
// #endregion

initFirst();
